/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useMemo } from 'react';
import type { Deal } from '@services/requests/housien-services/DealsService';
import { useClientDeals } from './useClientDeals';

/** Stages under which an attendance no longer counts as open. */
const SETTLED_STAGES = new Set(['won', 'lost']);

export interface ClientOpenAttendance {
  isLoading: boolean;
  /** Every open attendance, most recently updated first. Empty when none is open. */
  openAttendances: Deal[];
}

/**
 * The client's open attendances, if any, for the profile primary action.
 *
 * An attendance is open while its stage is neither `closed_won` nor
 * `closed_lost` -- every running stage and `paused` count. When the client
 * has several open, callers resume the most recently updated one by
 * default and let the person pick among the rest.
 *
 * @param agencyId - The owning agency.
 * @param clientId - The client to look up; empty disables the query.
 * @returns The view-model: a loading flag and the open attendances.
 */
export function useClientOpenAttendance(agencyId: string, clientId: string): ClientOpenAttendance {
  const { isLoading, deals } = useClientDeals(agencyId, clientId);

  const openAttendances = useMemo<Deal[]>(() => {
    const open = deals.filter((deal) => !SETTLED_STAGES.has(deal.stage));
    return [...open].sort((a, b) => (a.updated_at > b.updated_at ? -1 : 1));
  }, [deals]);

  return { isLoading, openAttendances };
}
